import { StoreRole } from '@app/shared';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorPage } from '@/pages/shared/error';
import { ROUTES } from '@/shared/config/routes';
import { GuestOnly, RequireAuth, RequireMembership, RootRedirect } from './guards';
import { InitialLoading } from './InitialLoading';
import { AuthLayout } from './layouts';
import { loadNamespace } from '@/shared/i18n/loadNamespace';

const OWNER_ROLES = [StoreRole.OWNER];
const CLIENT_ROLES = [StoreRole.CLIENT];

export const router = createBrowserRouter([
  {
    path: '/',
    HydrateFallback: InitialLoading,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <RootRedirect /> },

      // /login, /register, /forgot-password, /reset-password — redirect away if already signed in.
      {
        element: <GuestOnly />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'login',
                lazy: async () => {
                  const { LoginPage } = await import('@/pages/auth/login');
                  return { Component: LoginPage };
                },
              },
              {
                path: 'register',
                lazy: async () => {
                  const { RegisterPage } = await import('@/pages/auth/register');
                  return { Component: RegisterPage };
                },
              },
              {
                path: 'forgot-password',
                lazy: async () => {
                  const { ForgotPasswordPage } = await import('@/pages/auth/forgot-password');
                  return { Component: ForgotPasswordPage };
                },
              },
              {
                path: 'reset-password',
                lazy: async () => {
                  const { ResetPasswordPage } = await import('@/pages/auth/reset-password');
                  return { Component: ResetPasswordPage };
                },
              },
            ],
          },
        ],
      },

      // Every authenticated zone.
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'verify',
                lazy: async () => {
                  const { VerifyPage } = await import('@/pages/auth/verify');
                  return { Component: VerifyPage };
                },
              },
            ],
          },
          {
            path: 'create-store',
            lazy: async () => {
              const { CreateStorePage } = await import('@/pages/create-store');
              return { Component: CreateStorePage };
            },
          },
          {
            path: 'onboarding',
            lazy: async () => {
              const { OnboardingPage } = await import('@/pages/onboarding');
              return { Component: OnboardingPage };
            },
          },
          {
            path: 'join',
            lazy: async () => {
              const { JoinPage } = await import('@/pages/join');
              return { Component: JoinPage };
            },
          },
          {
            path: 'connections',
            lazy: async () => {
              const { ConnectionsPage } = await import('@/pages/connections');
              return { Component: ConnectionsPage };
            },
          },
          {
            path: 'admin',
            element: <RequireMembership roles={OWNER_ROLES} />,
            children: [
              {
                lazy: async () => {
                  const [{ AdminLayout }] = await Promise.all([
                    import('./layouts/AdminLayout'),
                    loadNamespace('admin'),
                  ]);
                  return { Component: AdminLayout };
                },
                children: [
                  // Dashboard is parked for now: `/admin` lands on the products page.
                  // {
                  //   index: true,
                  //   lazy: async () => {
                  //     const { AdminDashboardPage } = await import('@/pages/admin/dashboard');
                  //     return { Component: AdminDashboardPage };
                  //   },
                  // },
                  { index: true, element: <Navigate to={ROUTES.admin.products} replace /> },
                  {
                    path: 'categories',
                    lazy: async () => {
                      const { CategoriesPage } = await import('@/pages/admin/categories');
                      return { Component: CategoriesPage };
                    },
                  },
                  {
                    path: 'suppliers',
                    lazy: async () => {
                      const { SuppliersPage } = await import('@/pages/admin/suppliers');
                      return { Component: SuppliersPage };
                    },
                  },
                  {
                    path: 'products',
                    lazy: async () => {
                      const { ProductsListPage } = await import('@/pages/admin/products/list');
                      return { Component: ProductsListPage };
                    },
                  },
                  {
                    path: 'products/new',
                    lazy: async () => {
                      const { ProductQuickAddPage } =
                        await import('@/pages/admin/products/quick-add');
                      return { Component: ProductQuickAddPage };
                    },
                  },
                  {
                    path: 'products/:id',
                    lazy: async () => {
                      const { ProductFormPage } = await import('@/pages/admin/products/form');
                      return { Component: ProductFormPage };
                    },
                  },
                  {
                    path: 'settings',
                    lazy: async () => {
                      const { SettingsPage } = await import('@/pages/admin/settings');
                      return { Component: SettingsPage };
                    },
                  },
                  {
                    path: 'stock',
                    lazy: async () => {
                      const { StockLevelsPage } = await import('@/pages/admin/stock/levels');
                      return { Component: StockLevelsPage };
                    },
                  },
                  {
                    path: 'stock/write-offs',
                    lazy: async () => {
                      const { WriteOffsPage } = await import('@/pages/admin/stock/write-offs');
                      return { Component: WriteOffsPage };
                    },
                  },
                  {
                    path: 'stock/movements',
                    lazy: async () => {
                      const { MovementsPage } = await import('@/pages/admin/stock/movements');
                      return { Component: MovementsPage };
                    },
                  },
                  {
                    path: 'stock/products/:id',
                    lazy: async () => {
                      const { StockProductPage } = await import('@/pages/admin/stock/product');
                      return { Component: StockProductPage };
                    },
                  },
                  {
                    path: 'clients',
                    lazy: async () => {
                      const { ClientsPage } = await import('@/pages/admin/clients');
                      return { Component: ClientsPage };
                    },
                  },
                  {
                    path: 'orders',
                    lazy: async () => {
                      const { OrdersPage } = await import('@/pages/admin/orders');
                      return { Component: OrdersPage };
                    },
                  },
                  // The «new order» form is a window now (opened from the orders page); the old URL lands on the list.
                  { path: 'orders/new', element: <Navigate to={ROUTES.admin.orders} replace /> },
                  {
                    path: 'orders/:id',
                    lazy: async () => {
                      const { OrderDetailPage } = await import('@/pages/admin/order-detail');
                      return { Component: OrderDetailPage };
                    },
                  },
                  {
                    path: 'payments',
                    lazy: async () => {
                      const { PaymentsPage } = await import('@/pages/admin/payments');
                      return { Component: PaymentsPage };
                    },
                  },
                  {
                    path: 'reports',
                    lazy: async () => {
                      const { ReportsPage } = await import('@/pages/admin/reports');
                      return { Component: ReportsPage };
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'shop',
            element: <RequireMembership roles={CLIENT_ROLES} />,
            children: [
              {
                lazy: async () => {
                  const [{ ShopLayout }] = await Promise.all([
                    import('./layouts/ShopLayout'),
                    loadNamespace('client'),
                  ]);
                  return { Component: ShopLayout };
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { ShopHomePage } = await import('@/pages/shop/home');
                      return { Component: ShopHomePage };
                    },
                  },
                  {
                    path: 'c/:categoryId',
                    lazy: async () => {
                      const { ShopCategoryPage } = await import('@/pages/shop/category');
                      return { Component: ShopCategoryPage };
                    },
                  },
                  {
                    path: 'cart',
                    lazy: async () => {
                      const { ShopCartPage } = await import('@/pages/shop/cart');
                      return { Component: ShopCartPage };
                    },
                  },
                  {
                    path: 'orders',
                    lazy: async () => {
                      const { ShopOrdersPage } = await import('@/pages/shop/orders');
                      return { Component: ShopOrdersPage };
                    },
                  },
                  {
                    path: 'orders/:id',
                    lazy: async () => {
                      const { ShopOrderDetailPage } = await import('@/pages/shop/order-detail');
                      return { Component: ShopOrderDetailPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        path: 'no-access',
        lazy: async () => {
          const { NoAccessPage } = await import('@/pages/shared/no-access');
          return { Component: NoAccessPage };
        },
      },
      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('@/pages/shared/not-found');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
